import { IdPAdapter } from '../adapters/IdPAdapter'
import { StorageAdapter } from '../adapters/StorageAdapter'
import { transition } from '../machine/transition'
import { AuthEvent, AuthState, initialAuthState, Session } from '../machine/types'
import { createSingleFlight } from './singleFlight'
import { stabilizeRefreshedSession } from './stabilizeSession'

/**
 * Logger contract — kept tiny so apps can pipe to Sentry, console, or noop.
 * No level filtering here; the consumer's logger is expected to gate.
 */
export interface AuthLogger {
    debug: (msg: string, meta?: Record<string, unknown>) => void
    info: (msg: string, meta?: Record<string, unknown>) => void
    warn: (msg: string, meta?: Record<string, unknown>) => void
    error: (msg: string, error?: unknown, meta?: Record<string, unknown>) => void
}

export const noopLogger: AuthLogger = {
    debug: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: () => undefined,
}

export interface AuthRuntimeConfig {
    adapter: IdPAdapter
    /** Optional persistent storage. Without it, sessions live only in memory. */
    storage?: StorageAdapter
    /** Storage key used to persist the session blob. Default: `aos-auth:session`. */
    storageKey?: string
    /** Clock source — overridable for tests. Default: `Date.now`. */
    now?: () => number
    /** Single-flight cooldown after a refresh failure (ms). Default: 2_000. */
    refreshCooldownMs?: number
    /** Logger — defaults to a noop. */
    logger?: AuthLogger
    /**
     * If a token is within this many ms of expiry, treat it as expired
     * (refresh preemptively / on access). Default: 60_000.
     */
    refreshBufferMs?: number
    onBeforeLogout?: (session: Session) => Promise<void>
    /** Cap on `onBeforeLogout`. Default: 5_000. */
    onBeforeLogoutTimeoutMs?: number
}

export type Listener = (state: AuthState) => void
export type Unsubscribe = () => void

const DEFAULT_STORAGE_KEY = 'aos-auth:session'
const DEFAULT_REFRESH_BUFFER_MS = 60_000
const DEFAULT_ON_BEFORE_LOGOUT_TIMEOUT_MS = 5_000

/**
 * Side-effecting driver that owns the auth lifecycle.
 *
 * Responsibilities:
 * 1. Drive the pure {@link transition} function based on adapter results.
 * 2. Persist the session via the storage adapter.
 * 3. Single-flight refresh so concurrent 401s share one network round-trip.
 * 4. Expose a synchronous read API for hooks plus an event stream for
 *    cross-component coordination.
 *
 * The runtime is framework-agnostic; the React layer (`AuthProvider`)
 * subscribes to it and republishes its state via context.
 */
export class AuthRuntime {
    private state: AuthState = initialAuthState
    private logoutPromise: Promise<void> | null = null
    private refreshOperation: Promise<Session | null> | null = null
    private listeners = new Set<Listener>()
    // Initialised in the constructor body — not as a field initialiser —
    // because we read `this.config`. With Babel's class-property transform
    // (rspack pipeline), field initialisers run BEFORE parameter
    // properties are assigned, so `this.config` is `undefined` at the
    // field-init point and `cooldownMs: this.config.refreshCooldownMs`
    // throws `Cannot read properties of undefined`. tsc's emit happens to
    // hoist parameter properties first, which is why the unit tests
    // (ts-jest) never caught it. See AOS-535 follow-up.
    private readonly singleFlight: ReturnType<typeof createSingleFlight<Session>>
    private readonly logger: AuthLogger
    private readonly storageKey: string
    private readonly refreshBufferMs: number

    constructor(private readonly config: AuthRuntimeConfig) {
        this.logger = config.logger ?? noopLogger
        this.storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY
        this.refreshBufferMs = config.refreshBufferMs ?? DEFAULT_REFRESH_BUFFER_MS
        this.singleFlight = createSingleFlight<Session>({
            cooldownMs: config.refreshCooldownMs ?? 2_000,
            now: () => (config.now ?? Date.now)(),
        })
    }

    /** Current state — read synchronously. */
    getState(): AuthState {
        return this.state
    }

    /** Subscribe to state changes. Returns an unsubscribe. */
    subscribe(listener: Listener): Unsubscribe {
        this.listeners.add(listener)
        return () => {
            this.listeners.delete(listener)
        }
    }

    /**
     * Initialize the runtime. Reads any persisted session, calls
     * `adapter.init()`, and lands the machine in `authenticated` or
     * `unauthenticated`.
     */
    async init(): Promise<void> {
        this.dispatch({ type: 'INIT_STARTED' })
        let restored: Session | null = null
        let stale: Session | null = null

        if (this.config.storage) {
            const raw = await this.config.storage.get(this.storageKey)
            if (raw) {
                try {
                    const parsed = JSON.parse(raw) as Session
                    if (this.isSessionLive(parsed)) {
                        restored = parsed
                    } else if (parsed.refreshToken) {
                        stale = parsed
                    } else {
                        this.logger.debug('Persisted session expired; discarding')
                        await this.config.storage.remove(this.storageKey)
                    }
                } catch (err) {
                    this.logger.warn('Corrupt persisted session; discarding', {
                        error: String(err),
                    })
                    await this.config.storage.remove(this.storageKey)
                }
            }
        }

        if (restored) {
            this.dispatch({ type: 'INIT_RESOLVED', session: restored })
            this.logger.info('Session restored from storage')
            return
        }

        // AOS-774: the access token outlives its 5-minute lifespan far sooner
        // than the refresh token does, so a cold start inside that window must
        // spend the refresh token rather than throw the session away. `refresh()`
        // owns both outcomes: a rejected refresh token clears storage and lands
        // in `reauthRequired`, while a transient failure holds the session so a
        // launch without connectivity does not log the user out.
        //
        // The refresh is deliberately NOT awaited: `init()` gates the first
        // paint (`AuthProvider` defaults to `waitForInit`, mobile renders no
        // fallback), and AppAuth's token request inherits the OS timeout, so
        // awaiting it turns a dead network into a minute of blank screen.
        // `getAccessToken()` refreshes lazily before the first authorized call
        // anyway, so boot reaches a terminal state immediately either way.
        if (stale) {
            this.dispatch({ type: 'INIT_RESOLVED', session: stale })
            this.logger.info('Persisted session expired; refreshing in the background')
            void this.refresh()
            return
        }

        try {
            const result = await this.config.adapter.init()
            if (result.session) {
                await this.persist(result.session)
            }
            this.dispatch({ type: 'INIT_RESOLVED', session: result.session })
            this.logger.info('Adapter init resolved', {
                supported: result.supported,
                hasSession: !!result.session,
            })
        } catch (err) {
            this.logger.error('Adapter init failed', err)
            this.dispatch({ type: 'INIT_RESOLVED', session: null })
        }
    }

    /** Trigger interactive login. */
    async login(): Promise<void> {
        if (this.state.tag === 'authenticated' || this.state.tag === 'authenticating') {
            return
        }
        this.dispatch({ type: 'LOGIN_STARTED' })
        try {
            const session = await this.config.adapter.login()
            await this.persist(session)
            this.dispatch({ type: 'LOGIN_SUCCEEDED', session })
            this.logger.info('Login succeeded', { user: session.claims.user.id })
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            this.logger.warn('Login failed', { error: error.message })
            this.dispatch({ type: 'LOGIN_FAILED', error })
        }
    }

    /**
     * Refresh the access token. Single-flight: concurrent callers receive
     * the same in-flight Promise.
     *
     * `force` guarantees a token minted after the call: an already in-flight
     * refresh may have been issued before a server-side claim change (e.g. a
     * profile `siteLocation` update), so it is awaited and then superseded
     * instead of joined. Resolves to `null` when no refreshed session was
     * obtained.
     */
    async refresh(options: { force?: boolean } = {}): Promise<Session | null> {
        if (options.force && this.refreshOperation) {
            await this.refreshOperation
            return this.refresh()
        }
        const current = this.currentSession()
        if (!current) {
            return null
        }
        if (this.state.tag === 'refreshing') {
            return this.state.refreshPromise.catch(() => null)
        }

        const operation = this.runRefresh(current).finally(() => {
            if (this.refreshOperation === operation) {
                this.refreshOperation = null
            }
        })
        this.refreshOperation = operation
        return operation
    }

    private async runRefresh(current: Session): Promise<Session | null> {
        const promise = this.singleFlight.run(() =>
            this.config.adapter.refresh(current.refreshToken),
        )
        this.dispatch({ type: 'REFRESH_STARTED', promise })

        try {
            const refreshed = await promise
            // Reuse the previous claims references when contents
            // are unchanged, so reference-keyed consumers (effect deps,
            // reselect, the Redux session bridge) treat a silent refresh as
            // a no-op. The expiry timestamp and the tokens are always taken
            // from the fresh session.
            const session = stabilizeRefreshedSession(current, refreshed)
            await this.persist(session)
            this.dispatch({ type: 'REFRESH_SUCCEEDED', session })
            this.logger.info('Refresh succeeded')
            return session
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            if (this.config.adapter.isAuthError(error)) {
                await this.clearPersisted()
                this.dispatch({ type: 'REFRESH_FAILED_TERMINAL', error })
                this.logger.warn('Refresh failed terminally; reauth required', {
                    error: error.message,
                })
            } else {
                this.dispatch({ type: 'REFRESH_FAILED_TRANSIENT', error })
                this.logger.warn('Refresh failed transiently; holding session', {
                    error: error.message,
                })
            }
            return null
        }
    }

    /**
     * Mark the current session as invalidated by the server (e.g. 401 after
     * a retry has already exhausted refresh). Moves to `reauthRequired`.
     */
    async invalidate(error: Error): Promise<void> {
        if (this.state.tag !== 'authenticated' && this.state.tag !== 'refreshing') {
            return
        }
        await this.clearPersisted()
        this.dispatch({ type: 'SESSION_INVALIDATED', error })
        this.logger.warn('Session invalidated by server', { error: error.message })
    }

    /** Sign out at the IdP and clear local state. */
    async logout(): Promise<void> {
        if (this.logoutPromise) {
            return this.logoutPromise
        }
        this.logoutPromise = this.runLogout().finally(() => {
            this.logoutPromise = null
        })
        return this.logoutPromise
    }

    private async runLogout(): Promise<void> {
        const session = this.currentSession()
        if (session && this.config.onBeforeLogout) {
            await this.runBeforeLogout(session, this.config.onBeforeLogout)
        }
        this.dispatch({ type: 'LOGOUT_STARTED' })
        try {
            await this.config.adapter.logout(session?.idToken)
        } catch (err) {
            this.logger.warn('Adapter logout rejected; clearing local state anyway', {
                error: String(err),
            })
        }
        await this.clearPersisted()
        this.singleFlight.reset()
        this.dispatch({ type: 'LOGOUT_COMPLETED' })
        this.logger.info('Logout complete')
    }

    private async runBeforeLogout(
        session: Session,
        hook: (session: Session) => Promise<void>,
    ): Promise<void> {
        const timeoutMs = this.config.onBeforeLogoutTimeoutMs ?? DEFAULT_ON_BEFORE_LOGOUT_TIMEOUT_MS
        let timer: ReturnType<typeof setTimeout> | undefined
        try {
            await Promise.race([
                hook(session),
                new Promise<never>((_, reject) => {
                    timer = setTimeout(
                        () => reject(new Error(`onBeforeLogout timed out after ${timeoutMs}ms`)),
                        timeoutMs,
                    )
                }),
            ])
        } catch (err) {
            this.logger.warn('onBeforeLogout did not complete; continuing with logout', {
                error: String(err),
            })
        } finally {
            clearTimeout(timer)
        }
    }

    /** Best-effort access-token getter. Triggers refresh if near expiry. */
    async getAccessToken(): Promise<string | null> {
        const session = this.currentSession()
        if (!session) {
            return null
        }
        if (!this.isSessionLive(session)) {
            const refreshResult = await this.refresh()
            // Trust the adapter's freshly-issued session without re-checking liveness —
            // a successful refresh is assumed to always yield a live session. If that
            // assumption is ever wrong (misbehaving IdP), the outer 401-retry layer in
            // fetchConfigs.ts still self-heals.
            if (refreshResult !== null) {
                return refreshResult.accessToken
            }
            // Refresh failed; terminal or transient failure both leave no valid session
            return null
        }
        return session.accessToken
    }

    /** Synchronous read — returns whatever the runtime currently considers live. */
    currentSession(): Session | null {
        if (this.state.tag === 'authenticated' || this.state.tag === 'refreshing') {
            return this.state.session
        }
        return null
    }

    private dispatch(event: AuthEvent): void {
        const next = transition(this.state, event)
        if (next === this.state) {
            return
        }
        this.state = next
        for (const listener of this.listeners) {
            try {
                listener(next)
            } catch (err) {
                this.logger.error('Listener threw', err)
            }
        }
    }

    private async persist(session: Session): Promise<void> {
        if (!this.config.storage) {
            return
        }
        try {
            await this.config.storage.set(this.storageKey, JSON.stringify(session))
        } catch (err) {
            this.logger.warn('Failed to persist session', { error: String(err) })
        }
    }

    private async clearPersisted(): Promise<void> {
        if (!this.config.storage) {
            return
        }
        try {
            await this.config.storage.remove(this.storageKey)
        } catch (err) {
            this.logger.warn('Failed to clear persisted session', { error: String(err) })
        }
    }

    private isSessionLive(session: Session): boolean {
        const now = (this.config.now ?? Date.now)()
        return session.claims.expiresAt - this.refreshBufferMs > now
    }
}
